import * as React from 'react';
import { UIHeading4, UIText2, ModalWindow, Button } from '@cian/ui-kit';
import * as styles from './CadastralNumbers.css';
import { FormRow } from './components/FormRow';
import { Section } from './components/Section';
import { ModalContent, ModalFooter } from './components/ModalContent';
import { InformModalContent, InformModalFooter, InformModalHeader } from './components/InformModal';
import { ICadastralNumbersProps } from './types';
import { CadastralNumberInput } from './components/CadastralNumberInput';
import { disableChecking } from './utils';
import classNames from 'classnames';
import { trackCheckButtonClick } from './tracking';
import { ReasonModal } from './components/ReasonModal';
import { useDeviceCondition } from '../../hooks';
import { NBSP } from '../../constants/symbols';

export const CadastralNumbers: React.FC<ICadastralNumbersProps> = ({
  isLand,
  buildingNumber,
  landNumber,
  onChange,
  onSubmit,
  showNoCadastralNumberButton,
  destinationModalState: [destinationModalOpened, toggleDestinationModal],
  reasonModalState: [reasonModalOpened, openReasonModal, closeReasonModal],
  informModalState: [informModalOpened, toggleInformModal],
}) => {
  const headerClassName = useDeviceCondition(styles['row-header'], styles['column-header']);
  const saveButtonClassName = useDeviceCondition('', styles['mobile']);

  return (
    <div className={classNames(landNumber.isRequired && landNumber.error && 'cui-invalid')}>
      <Section>
        <div className={headerClassName}>
          <UIHeading4>Введите кадастровые номера</UIHeading4>
          <div className={styles['button-container']} role="button" onClick={toggleDestinationModal}>
            <UIText2 as="span" color="primary_100" display="inline">
              Где найти номера
            </UIText2>
          </div>
        </div>
      </Section>
      <Section>
        <div className={styles['informer']}>
          <UIText2>
            Покупатель не увидит кадастровый номер и{NBSP}точный адрес объекта, указанный в{NBSP}Росреестре. Мы покажем
            информацию о{NBSP}площади, этажности, количестве собственников и{NBSP}обременениях, если они
            {NBSP}есть. А{NBSP}ещё добавим значок «Проверено в{NBSP}Росреестре» к{NBSP}объявлению.
          </UIText2>
          <div className={styles['informer-button']} role="button" onClick={toggleInformModal}>
            <UIText2 as="span" color="primary_100" display="inline">
              Посмотреть пример
            </UIText2>
          </div>
        </div>
      </Section>
      <ModalWindow
        title="Где найти кадастровый номер"
        open={destinationModalOpened}
        onClose={toggleDestinationModal}
        width={604}
        footer={<ModalFooter />}
        content={<ModalContent />}
      />
      <ModalWindow
        title="Пример информации из Росреестра, которую увидит покупатель"
        open={informModalOpened}
        onClose={toggleInformModal}
        width={688}
        header={<InformModalHeader />}
        content={<InformModalContent />}
        footer={<InformModalFooter onClick={toggleInformModal} />}
        fixed
        escape
      />
      <form className={styles['field-container']} onSubmit={onSubmit}>
        <div className={styles['field']}>
          <FormRow
            label="Кадастровый номер участка"
            error={landNumber.error?.text}
            withoutMargin
            field={
              <CadastralNumberInput
                value={landNumber.value}
                onChange={onChange('cadastralNumber')}
                invalid={!!landNumber.error}
                checked={landNumber.isChecked}
                loading={landNumber.isChecking}
                required={landNumber.isRequired}
              />
            }
          >
            {showNoCadastralNumberButton && (
              <>
                <div className={styles['reason-button']} role="button" onClick={openReasonModal}>
                  <UIText2 as="span" color="primary_100" display="inline">
                    Не указывать номер участка
                  </UIText2>
                </div>
                <ReasonModal isOpen={reasonModalOpened} onSubmit={closeReasonModal} onClose={closeReasonModal} />
              </>
            )}
          </FormRow>
        </div>
        {!isLand && (
          <FormRow
            label="Кадастровый номер здания"
            error={buildingNumber.error?.text}
            className={styles['field']}
            field={
              <CadastralNumberInput
                value={buildingNumber.value}
                onChange={onChange('buildingCadastralNumber')}
                invalid={!!buildingNumber.error}
                checked={buildingNumber.isChecked}
                loading={buildingNumber.isChecking}
                required={buildingNumber.isRequired}
              />
            }
          />
        )}
        <div className={classNames(styles['submit-container'], saveButtonClassName)}>
          <Button
            type="submit"
            size="XS"
            theme="fill_primary"
            disabled={disableChecking(buildingNumber, landNumber)}
            loading={buildingNumber.isChecking || landNumber.isChecking}
            onClick={trackCheckButtonClick}
          >
            Сохранить
          </Button>
        </div>
      </form>
    </div>
  );
};
